import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type { TypedArray } from '@kitware/vtk.js/types';

import type { ContourExport } from '@/src/composables/useMaskExtractor';

import * as pako from 'pako';
import * as UTIF from 'utif';
import { ref } from 'vue';

import { useMaskExtractor } from '@/src/composables/useMaskExtractor';

import { ContourLabel } from '@/src/store/contours/contour-definitions';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { useAppState } from '@/src/store/ocg-app-state';

interface SelectedImage {
  uid: string;
  sliceIndex: number;
  phaseIndex: number;
}

interface SeriesExtractionGroup {
  seriesUid: string;
  seriesName: string;
  images: SelectedImage[];
}

export interface ExtractionRequest {
  seriesGroups: SeriesExtractionGroup[];
  contoursToExtract: ContourLabel[];
  maskType: 'binary' | 'signalIntensity';
  extractionMode: 'all' | 'isolate';
  extractStaticMasks: string[];
  extractOCGCurves: string[];
  extractPolarMaps: string[];
  imageMapping: Record<string, Record<string, string[]>>; // seriesUID -> sliceIndex -> imageUIDs
  imageUidIndexMapping: Record<string, Record<string, Record<string, number>>>;
  imageFormat: 'png' | 'tiff';
}

export function useImageExtractor() {
  const appState = useAppState();
  const currentStudyStore = useCurrentStudyStore();
  const { getImageMaskPixels, detectImageOrientation } = useMaskExtractor();

  const isProcessing = ref(false);
  const progress = ref(0);
  const status = ref('');
  const generatedZipBlob = ref<Blob | null>(null);

  const worker = new Worker(
    new URL('../io/image-export.worker.ts', import.meta.url),
    { type: 'module' },
  );

  const sanitizePathSegment = (value: string) =>
    value
      .trim()
      .replace(/[\\/:*?"<>|]/g, '_')
      .replace(/\s+/g, ' ')
      .replace(/\.+$/g, '') || 'Unknown';

  const formatExtractionStamp = (date: Date) => {
    const pad = (value: number) => String(value).padStart(2, '0');
    return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(
      date.getDate(),
    )}_${pad(date.getHours())}${pad(date.getMinutes())}${pad(
      date.getSeconds(),
    )}`;
  };

  /**
   * Build mask exports for a given image using the shared mask extractor.
   * Auto-detects orientation from existing contour labels and uses
   * the first available viewer for transmurality offsets.
   */
  const buildDefaultContourExports = (imageUID: string): ContourExport[] => {
    const orientation = detectImageOrientation(imageUID);
    // Use ContoursLeft as the default viewer for transmurality offsets
    return getImageMaskPixels(imageUID, orientation, 'ContoursLeft');
  };

  const encodeGrayscaleTIFF = (
    grayBytes: Uint8Array,
    w: number,
    h: number,
  ): Blob => {
    const idf = {
      t256: [w],
      t257: [h],
      t258: [8],
      t259: [1],
      t262: [1], // BlackIsZero (Grayscale)
      t273: [1000], // StripOffsets
      t277: [1], // SamplesPerPixel (1 channel)
      t278: [h],
      t279: [w * h], // StripByteCounts
    };
    const prfx = new Uint8Array(UTIF.encode([idf]));
    const data = new Uint8Array(1000 + w * h);
    data.set(prfx);
    data.set(grayBytes, 1000);
    return new Blob([data.buffer], { type: 'image/tiff' });
  };

  const createGrayscalePngBlob = (
    pixels: Uint8Array,
    width: number,
    height: number,
  ): Blob => {
    const uncompressed = new Uint8Array((width + 1) * height);
    let srcOffset = 0;
    let destOffset = 0;
    for (let y = 0; y < height; y++) {
      uncompressed[destOffset++] = 0; // Filter type 0 (None)
      for (let x = 0; x < width; x++) {
        uncompressed[destOffset++] = pixels[srcOffset++];
      }
    }

    const compressed = pako.deflate(uncompressed);

    const crcTable: number[] = [];
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) {
        c = c & 1 ? 0xEDB88320 ^ (c >>> 1) : c >>> 1;
      }
      crcTable[n] = c;
    }

    function calculateCrc(data: Uint8Array): number {
      let crc = -1;
      for (let i = 0; i < data.length; i++) {
        crc = (crc >>> 8) ^ crcTable[(crc ^ data[i]) & 0xFF];
      }
      return (crc ^ -1) >>> 0;
    }

    function writeUint32(buf: Uint8Array, offset: number, val: number) {
      buf[offset] = (val >>> 24) & 0xFF;
      buf[offset + 1] = (val >>> 16) & 0xFF;
      buf[offset + 2] = (val >>> 8) & 0xFF;
      buf[offset + 3] = val & 0xFF;
    }

    const signature = new Uint8Array([
      0x89,
      0x50,
      0x4E,
      0x47,
      0x0D,
      0x0A,
      0x1A,
      0x0A,
    ]);

    const ihdrChunk = new Uint8Array(4 + 4 + 13 + 4);
    writeUint32(ihdrChunk, 0, 13);
    ihdrChunk[4] = 0x49; // I
    ihdrChunk[5] = 0x48; // H
    ihdrChunk[6] = 0x44; // D
    ihdrChunk[7] = 0x52; // R
    writeUint32(ihdrChunk, 8, width);
    writeUint32(ihdrChunk, 12, height);
    ihdrChunk[16] = 8; // Bit depth: 8
    ihdrChunk[17] = 0; // Color type: 0 (grayscale)
    ihdrChunk[18] = 0; // Compression method: 0
    ihdrChunk[19] = 0; // Filter method: 0
    ihdrChunk[20] = 0; // Interlace method: 0
    const ihdrCrc = calculateCrc(ihdrChunk.subarray(4, 21));
    writeUint32(ihdrChunk, 21, ihdrCrc);

    const idatChunk = new Uint8Array(4 + 4 + compressed.length + 4);
    writeUint32(idatChunk, 0, compressed.length);
    idatChunk[4] = 0x49; // I
    idatChunk[5] = 0x44; // D
    idatChunk[6] = 0x41; // A
    idatChunk[7] = 0x54; // T
    idatChunk.set(compressed, 8);
    const idatCrc = calculateCrc(idatChunk.subarray(4, 8 + compressed.length));
    writeUint32(idatChunk, 8 + compressed.length, idatCrc);

    const iendChunk = new Uint8Array([
      0x00,
      0x00,
      0x00,
      0x00,
      0x49,
      0x45,
      0x4E,
      0x44,
      0xAE,
      0x42,
      0x60,
      0x82,
    ]);

    const totalLength
      = signature.length + ihdrChunk.length + idatChunk.length + iendChunk.length;
    const pngData = new Uint8Array(totalLength);
    let offset = 0;
    pngData.set(signature, offset);
    offset += signature.length;
    pngData.set(ihdrChunk, offset);
    offset += ihdrChunk.length;
    pngData.set(idatChunk, offset);
    offset += idatChunk.length;
    pngData.set(iendChunk, offset);

    return new Blob([pngData.buffer], { type: 'image/png' });
  };

  const drawMaskOnCanvas = async (
    points: Array<{ x: number; y: number; si: number }>,
    width: number,
    height: number,
    type: 'binary' | 'signalIntensity',
    format: 'png' | 'tiff',
    // opaqueBackground: boolean = true
  ): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const maskData = new Uint8Array(width * height);

      let minSI = Infinity;
      let maxSI = -Infinity;
      if (type === 'signalIntensity') {
        points.forEach((p) => {
          if (p.si < minSI) minSI = p.si;
          if (p.si > maxSI) maxSI = p.si;
        });
      }

      points.forEach((p) => {
        const { x, y, si } = p;
        if (x < 0 || x >= width || y < 0 || y >= height) return;

        const index = y * width + x;

        if (type === 'binary') {
          maskData[index] = 255;
        }
        else {
          const range = maxSI - minSI;
          const normalized = range === 0 ? 0 : ((si - minSI) / range) * 255;
          maskData[index] = Math.round(normalized);
        }
      });

      if (format === 'tiff') {
        resolve(encodeGrayscaleTIFF(maskData, width, height));
      }
      else {
        resolve(createGrayscalePngBlob(maskData, width, height));
      }
    });
  };

  const getImageAsBlob = async (
    uid: string,
    width: number,
    height: number,
    vtkImage: vtkImageData,
    format: 'png' | 'tiff',
  ): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const scalars = vtkImage.getPointData()?.getScalars()?.getData() as TypedArray | undefined;
      if (!scalars) {
        resolve(null);
        return;
      }

      let min = Infinity;
      let max = -Infinity;
      for (let i = 0; i < scalars.length; i++) {
        const val = scalars[i];
        if (val < min) min = val;
        if (val > max) max = val;
      }
      const range = max - min;

      const grayscaleData = new Uint8Array(scalars.length);
      for (let i = 0; i < scalars.length; i++) {
        const val = scalars[i];
        const normalized = range > 0 ? ((val - min) / range) * 255 : 0;
        grayscaleData[i] = Math.round(normalized);
      }

      if (format === 'tiff') {
        resolve(encodeGrayscaleTIFF(grayscaleData, width, height));
      }
      else {
        resolve(createGrayscalePngBlob(grayscaleData, width, height));
      }
    });
  };

  async function yieldToUI() {
    return new Promise<void>((resolve) => {
      setTimeout(resolve, 0);
    });
  }

  async function processDiscoveryImagesForSeries(params: {
    seriesGroup: SeriesExtractionGroup;
    seriesImageDataCache: Map<string, vtkImageData>;
    seriesMaskNames: Set<string>;
    updateExtractionFeedback: (message: string) => void;
    onImageLoaded: () => void;
  }) {
    const {
      seriesGroup,
      seriesImageDataCache,
      seriesMaskNames,
      updateExtractionFeedback,
      onImageLoaded,
    } = params;

    await seriesGroup.images.reduce<Promise<void>>(
      async (chain, selectedImage, imageIndex) =>
        chain.then(async () => {
          updateExtractionFeedback(
            `Loading image ${imageIndex + 1}/${seriesGroup.images.length} for ${
              seriesGroup.seriesName
            }...`,
          );
          await yieldToUI();

          const cachedImageData
            = await currentStudyStore.fetchOrGetVtkImageData(selectedImage.uid);

          onImageLoaded();

          if (!cachedImageData) {
            return;
          }

          seriesImageDataCache.set(selectedImage.uid, cachedImageData);
          buildDefaultContourExports(selectedImage.uid).forEach(
            (contourExport) => {
              seriesMaskNames.add(contourExport.name);
            },
          );
        }),
      Promise.resolve(),
    );
  }

  async function processSeriesGroup(params: {
    seriesGroup: SeriesExtractionGroup;
    studyName: string;
    extractionFolder: string;
    format: 'png' | 'tiff';
    maskType: 'binary' | 'signalIntensity';
    filesToZip: Array<{ name: string; data: Blob }>;
    progressState: { completedWorkUnits: number; totalWorkUnits: number };
    imageUidIndexMapping: ExtractionRequest['imageUidIndexMapping'];
    updateExtractionFeedback: (message: string) => void;
  }) {
    const {
      seriesGroup,
      studyName,
      extractionFolder,
      format,
      maskType,
      filesToZip,
      progressState,
      imageUidIndexMapping,
      updateExtractionFeedback,
    } = params;

    const seriesFolder = `${studyName}/${extractionFolder}/Post-HV_Breath-Hold`;
    updateExtractionFeedback(`Processing series ${seriesGroup.seriesName}...`);

    const seriesImageDataCache = new Map<string, vtkImageData>();
    const seriesMaskNames = new Set<string>();

    await processDiscoveryImagesForSeries({
      seriesGroup,
      seriesImageDataCache,
      seriesMaskNames,
      updateExtractionFeedback,
      onImageLoaded: () => {
        progressState.completedWorkUnits += 1;
        progress.value = Math.min(
          (progressState.completedWorkUnits / progressState.totalWorkUnits)
          * 100,
          99.5,
        );
      },
    });

    if (seriesMaskNames.size === 0) {
      return;
    }

    // Use appearance indexing so always start from 0 for slice and phase
    const sliceMap = new Map<number, number>();
    const phaseMaps = new Map<number, Map<number, number>>();

    let nextSliceIndex = 0;

    for (
      let imageIndex = 0;
      imageIndex < seriesGroup.images.length;
      imageIndex++
    ) {
      const selectedImage = seriesGroup.images[imageIndex];
      const { uid, sliceIndex } = selectedImage;
      // const phaseIndex =
      //   request.imageUidIndexMapping[seriesGroup.seriesUid]?.[
      //     String(sliceIndex + 1)
      //   ]?.[uid] ?? selectedImage.phaseIndex;

      const actualPhaseIndex
        = imageUidIndexMapping[seriesGroup.seriesUid]?.[String(sliceIndex + 1)]?.[
          uid
        ] ?? selectedImage.phaseIndex;

      // Assign sequential slice index
      if (!sliceMap.has(sliceIndex)) {
        sliceMap.set(sliceIndex, nextSliceIndex++);
      }

      const sequentialSliceIndex = sliceMap.get(sliceIndex)!;

      // Assign sequential phase index within this slice
      if (!phaseMaps.has(sliceIndex)) {
        phaseMaps.set(sliceIndex, new Map());
      }

      const phaseMap = phaseMaps.get(sliceIndex)!;

      if (!phaseMap.has(actualPhaseIndex)) {
        phaseMap.set(actualPhaseIndex, phaseMap.size);
      }

      const sequentialPhaseIndex = phaseMap.get(actualPhaseIndex)!;
      updateExtractionFeedback(
        `Extracting image ${imageIndex + 1}/${seriesGroup.images.length} for ${
          seriesGroup.seriesName
        }...`,
      );
      const imageData: vtkImageData | undefined
        = seriesImageDataCache.get(uid)
          || (await currentStudyStore.fetchOrGetVtkImageData(uid));

      if (!imageData) {
        console.warn(`Image data not found for UID: ${uid}`);

        continue;
      }

      const dims = imageData.getDimensions();
      const width = dims[0];
      const height = dims[1];

      // 1. Extract Image

      const imageBlob = await getImageAsBlob(
        uid,
        width,
        height,
        imageData,
        format,
      );
      if (imageBlob) {
        filesToZip.push({
          name: `${seriesFolder}/ImageData/s${sequentialSliceIndex}p${sequentialPhaseIndex}_data.${format}`,
          data: imageBlob,
        });
      }

      // 2. Extract default contour-derived masks for the image.
      const contourExports = buildDefaultContourExports(uid);
      const contourExportMap = new Map(
        contourExports.map(contourExport => [
          contourExport.name,
          contourExport.points,
        ]),
      );

      for (const maskTypeName of seriesMaskNames) {
        const points = contourExportMap.get(maskTypeName) || [];

        const blob = await drawMaskOnCanvas(
          points,
          width,
          height,
          maskType,
          'png',
        );
        if (blob) {
          const maskTypeFolder = sanitizePathSegment(maskTypeName);
          filesToZip.push({
            name: `${seriesFolder}/MaskData/${maskTypeFolder}s/s${sequentialSliceIndex}p${sequentialPhaseIndex}_${maskTypeFolder}.png`,
            data: blob,
          });
        }
      }

      progressState.completedWorkUnits += 1;
      progress.value = Math.min(
        (progressState.completedWorkUnits / progressState.totalWorkUnits) * 100,
        99.5,
      );
    }

    updateExtractionFeedback(
      `Finished series ${seriesGroup.seriesName}. Preparing zip output...`,
    );
  }

  const extractAndZip = async (request: ExtractionRequest) => {
    isProcessing.value = true;
    progress.value = 0;
    status.value = 'Starting extraction...';
    generatedZipBlob.value = null;

    const filesToZip: Array<{ name: string; data: Blob }> = [];
    const totalImages = request.seriesGroups.reduce(
      (sum, group) => sum + group.images.length,
      0,
    );
    const totalWorkUnits = Math.max(totalImages * 2, 1);
    const progressState = { completedWorkUnits: 0, totalWorkUnits };
    const format = request.imageFormat || 'png';

    const updateExtractionFeedback = (message: string) => {
      status.value = message;
      progress.value = Math.min(
        (progressState.completedWorkUnits / progressState.totalWorkUnits) * 100,
        99.5,
      );
    };

    try {
      const now = new Date();
      const studyInfo = currentStudyStore.getStudyInfo;
      const studyName = sanitizePathSegment(
        studyInfo.PatientName
        || studyInfo.StudyDescription
        || studyInfo.PatientID
        || 'Unknown Study',
      );
      // const extractionFolder = `OCG19_${formatExtractionStamp(now)}`;
      const extractionFolder = `RUN_${formatExtractionStamp(now)}`;

      await request.seriesGroups.reduce<Promise<void>>(
        async (chain, seriesGroup) =>
          chain.then(async () =>
            processSeriesGroup({
              seriesGroup,
              studyName,
              extractionFolder,
              format,
              maskType: request.maskType,
              filesToZip,
              progressState,
              imageUidIndexMapping: request.imageUidIndexMapping,
              updateExtractionFeedback,
            }),
          ),
        Promise.resolve(),
      );

      // 4. Create Metadata JSON
      const appVersion = appState.appVersionText;
      const metadata = {
        scanner: {
          manufacturer: studyInfo.Manufacturer || 'Unknown',
          modality: studyInfo.Modality || 'Unknown',
        },
        study: {
          patientID: studyInfo.PatientID || studyInfo.PatientName || 'Unknown',
          studyUID: currentStudyStore.getStudyID || 'Unknown',
          studyDate: studyInfo.StudyDate || 'Unknown',
          studyTime: studyInfo.StudyTime || 'Unknown',
          studyDescription: studyInfo.StudyDescription || 'Unknown',
        },
        extractionDate: now.toLocaleDateString(),
        extractionTime: now.toLocaleTimeString(),
        requestedBy: appState.currentUserName || 'Unknown User',
        appVersion: appVersion || 'Unknown Version',
        imageMapping: request.imageMapping,
      };

      const metadataBlob = new Blob([JSON.stringify(metadata, null, 2)], {
        type: 'application/json',
      });
      filesToZip.push({
        name: 'metadata.json',
        data: metadataBlob,
      });

      //  5. Create a DEMOG.csv file with the following: ID, Label
      // ID corresponds to the studyName and Label keep as 0 for all
      const demogContent = `ID,Label\n${studyName},0\n`;
      const demogBlob = new Blob([demogContent], { type: 'text/csv' });
      filesToZip.push({
        name: 'DEMOG.csv',
        data: demogBlob,
      });

      status.value = 'Zipping files...';
      progress.value = 99.5;

      return await new Promise<void>((resolve, reject) => {
        worker.onmessage = (e: MessageEvent<{ type: string; blob?: Blob; error?: string }>) => {
          if (e.data.type === 'zipComplete') {
            if (e.data.blob) {
              generatedZipBlob.value = e.data.blob;
            }

            isProcessing.value = false;
            progress.value = 100;
            status.value = 'Extraction Complete! Ready to download.';
            resolve();
          }
          else if (e.data.type === 'error') {
            isProcessing.value = false;
            status.value = `Error: ${e.data.error ?? 'Unknown error'}`;
            reject(new Error(e.data.error ?? 'Unknown error'));
          }
        };

        worker.postMessage({
          type: 'zip',
          files: filesToZip,
        });
      });
    }
    catch (e) {
      isProcessing.value = false;
      status.value = `Error: ${(e as Error).message}`;
      console.error(e);
    }
  };

  const downloadZip = () => {
    const studyInfo = currentStudyStore.getStudyInfo;
    const studyName = sanitizePathSegment(
      studyInfo.PatientName
      || studyInfo.StudyDescription
      || studyInfo.PatientID
      || 'Unknown Study',
    );
    const dateTime = new Date().toISOString().replace(/[:.]/g, '');
    const filename = `${studyName}_${dateTime}.zip`;
    if (!generatedZipBlob.value) return;
    const url = URL.createObjectURL(generatedZipBlob.value);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return {
    extractAndZip,
    downloadZip,
    getImageAsBlob,
    isProcessing,
    progress,
    status,
    generatedZipBlob,
  };
}
